/**
 * ColumnMapper - Component for mapping Excel columns to API parameters
 * author: Generated Component
 */
import React, { useState, useCallback, useMemo, useContext, useEffect } from 'react';
import { Row, Col, Card, Badge, Form, Button, Alert, Modal } from 'react-bootstrap';
import { Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { RegulationContext } from '../../App';
import StandardService from '../../core/service/StandardService';
import CompanyService from '../../core/service/CompanyService';

const ColumnMapper = ({ columns, mappings, onMappingChange, isProcessing, onImport, hasData, selectedSheet, t }) => {
  const [branchMappings, setBranchMappings] = useState([]);
  const [dataPointMappings, setDataPointMappings] = useState([]);
  const [showDataPointModal, setShowDataPointModal] = useState(false);
  const [dataPoints, setDataPoints] = useState([]);
  const [searchKey, setSearchKey] = useState('');
  const [isLoadingDataPoints, setIsLoadingDataPoints] = useState(false);
  
  // Filter datapoints locally based on search key
  const filteredDataPoints = useMemo(() => {
    if (!searchKey.trim()) return dataPoints;
    
    return dataPoints.filter(dataPoint => 
      dataPoint.code.toLowerCase().includes(searchKey.toLowerCase()) ||
      dataPoint.name.toLowerCase().includes(searchKey.toLowerCase())
    );
  }, [dataPoints, searchKey]);
  const [branches, setBranches] = useState([]);
  const [unitsOfMeasure, setUnitsOfMeasure] = useState([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [isLoadingUOMs, setIsLoadingUOMs] = useState(false);
  const [useAllDatapointsFromColumn, setUseAllDatapointsFromColumn] = useState(false);
  const { glbRegulation } = useContext(RegulationContext);

  // Initialize branch mappings when component mounts or columns change
  useMemo(() => {
    // Start with empty branch mappings - user will add manually
    if (columns.length > 0 && branchMappings.length === 0) {
      setBranchMappings([]);
    }
  }, [columns, branchMappings.length]);

  // Load branches from API
  const loadBranches = useCallback(async () => {
    setIsLoadingBranches(true);
    try {
      // Call both API endpoints
      const [response1, response2] = await Promise.all([
        CompanyService.getEnterprisesByLevel(1),
        CompanyService.getEnterprisesByLevel(2)
      ]);
      
      if (response1.status === 200 && response2.status === 200) {
        // Merge results from both endpoints
        const allBranches = [...response1.data, ...response2.data];
        
        // Sort by sortOrder
        const sortedBranches = allBranches.sort((a, b) => a.sortOrder - b.sortOrder);
        
        setBranches(sortedBranches);
      }
    } catch (error) {
    } finally {
      setIsLoadingBranches(false);
    }
  }, []);

  // Load UOMs from API
  const loadUOMs = useCallback(async () => {
    setIsLoadingUOMs(true);
    try {
      const response = await StandardService.getUnitsOfMeasure();
      if (response.status === 200) {
        const outputList = [];
        for (let index = 0; index < response.data.data.length; index++) {
          const element = response.data.data[index];
          outputList.push({
            id: element.id,
            label: element.outputUnitName,
            value: element.outputUnit
          });
        }
        const uniqueOutputArray = Array.from(new Set(outputList.map(obj => obj.value))).map(
          value => outputList.find(obj => obj.value === value)
        );
        uniqueOutputArray.sort((a, b) => a.value.localeCompare(b.value));
        setUnitsOfMeasure(uniqueOutputArray);
      }
    } catch (error) {
    } finally {
      setIsLoadingUOMs(false);
    }
  }, []);

  // Load branches and UOMs when component mounts
  useEffect(() => {
    loadBranches();
    loadUOMs();
  }, [loadBranches, loadUOMs]);

  // Update mappings when branch mappings change
  const updateMappings = useCallback((newBranchMappings, newDataPointMappings) => {
    const newMappings = { ...mappings };

    // Update branch mappings
    newMappings.branchMappings = newBranchMappings;

    // Update datapoint mappings
    newMappings.dataPointMappings = newDataPointMappings;

    onMappingChange(newMappings);
  }, [mappings, onMappingChange]);

  // Load datapoints from API
  const loadDataPoints = useCallback(async () => {
    if (!glbRegulation?.id) return;

    setIsLoadingDataPoints(true);
    try {
      const response = await StandardService.getAPageOfDps(
        glbRegulation.id,
        0,
        100, // Get first 100 datapoints
        false,
        true,
        "", // No search key - load all datapoints
        ""
      );
      setDataPoints(response.data || []);
    } catch (error) {
    } finally {
      setIsLoadingDataPoints(false);
    }
  }, [glbRegulation?.id]);

  // Load datapoints when modal opens
  useEffect(() => {
    if (showDataPointModal) {
      loadDataPoints();
    }
  }, [showDataPointModal, loadDataPoints]);

  const addDataPointMapping = useCallback(() => {
    const newMapping = {
      id: `datapoint-${Date.now()}`,
      dataPointId: '',
      dataPointCode: '',
      dataPointName: '',
      selectedRow: ''
    };
    const newDataPointMappings = [...dataPointMappings, newMapping];
    setDataPointMappings(newDataPointMappings);
    updateMappings(branchMappings, newDataPointMappings);
  }, [dataPointMappings, branchMappings, updateMappings]);

  const removeDataPointMapping = useCallback((id) => {
    const newDataPointMappings = dataPointMappings.filter(mapping => mapping.id !== id);
    setDataPointMappings(newDataPointMappings);
    updateMappings(branchMappings, newDataPointMappings);
  }, [dataPointMappings, branchMappings, updateMappings]);

  const updateDataPointMapping = useCallback((id, field, value) => {
    const newDataPointMappings = dataPointMappings.map(mapping => {
      if (mapping.id === id) {
        return { ...mapping, [field]: value };
      }
      return mapping;
    });
    setDataPointMappings(newDataPointMappings);
    updateMappings(branchMappings, newDataPointMappings);
  }, [dataPointMappings, branchMappings, updateMappings]);

  const handleDataPointCodeChange = useCallback((columnName) => {
    const newMappings = { ...mappings };
    if (columnName === 'skip') {
      delete newMappings.dataPointCode;
    } else {
      newMappings.dataPointCode = columnName;
    }
    newMappings.useAllDatapointsFromColumn = useAllDatapointsFromColumn;
    onMappingChange(newMappings);
  }, [mappings, onMappingChange, useAllDatapointsFromColumn]);

  const addBranchMapping = useCallback(() => {
    const newMapping = {
      id: `branch-${Date.now()}`,
      branchNameColumn: '',
      branchNameFromAPI: '',
      branchValuesColumn: '',
      branchUomColumn: '',
      branchUomFromAPI: '',
      branchNotesColumn: ''
    };
    const newBranchMappings = [...branchMappings, newMapping];
    setBranchMappings(newBranchMappings);
    updateMappings(newBranchMappings, dataPointMappings);
  }, [branchMappings, dataPointMappings, updateMappings]);

  const removeBranchMapping = useCallback((id) => {
    const newBranchMappings = branchMappings.filter(mapping => mapping.id !== id);
    setBranchMappings(newBranchMappings);
    updateMappings(newBranchMappings, dataPointMappings);
  }, [branchMappings, dataPointMappings, updateMappings]);

  const updateBranchMapping = useCallback((id, field, value) => {
    const newBranchMappings = branchMappings.map(mapping => {
      if (mapping.id === id) {
        return { ...mapping, [field]: value };
      }
      return mapping;
    });
    setBranchMappings(newBranchMappings);
    updateMappings(newBranchMappings, dataPointMappings);
  }, [branchMappings, dataPointMappings, updateMappings]);

  const getMappingStatus = () => {
    const hasBranchMappings = branchMappings.length > 0 &&
      branchMappings.some(mapping => (mapping.branchNameColumn || mapping.branchNameFromAPI) && mapping.branchValuesColumn);

    // Check if we have valid datapoint mappings based on checkbox state
    let hasDataPointMappings = false;
    if (useAllDatapointsFromColumn) {
      // When checkbox is checked, we need a datapoint column selected
      hasDataPointMappings = mappings.dataPointCode && mappings.dataPointCode !== '';
    } else {
      // When checkbox is unchecked, we need individual datapoint mappings
      hasDataPointMappings = dataPointMappings.length > 0 &&
        dataPointMappings.some(mapping => mapping.dataPointId && mapping.selectedRow);
    }

    return {
      branchData: hasBranchMappings,
      dataPointMappings: hasDataPointMappings,
      totalBranches: branchMappings.length,
      mappedBranches: branchMappings.filter(mapping => (mapping.branchNameColumn || mapping.branchNameFromAPI) && mapping.branchValuesColumn).length,
      totalDataPoints: dataPointMappings.length,
      mappedDataPoints: dataPointMappings.filter(mapping => mapping.dataPointId && mapping.selectedRow).length
    };
  };

  const status = getMappingStatus();
  const canImport = status.branchData && status.dataPointMappings;


  if (!hasData) {
    return (
      <div className="text-center py-5">
        <p className="text-muted">{t("ColumnMapper.noData.label")}</p>
      </div>
    );
  }

  const renderPreview = () => {
    return;
    /**Currently the below feature is not using, once the feature is implemented, we can uncomment the code below **/
//     return (
//       <>
//         {canImport && (
//           <Row className="mb-4">
//             <Col>
//               <Card className="mapping-card">
//                 <Card.Header>
//                   <h6 className="mb-0">{t("ColumnMapper.apiPayloadPreview.title.label")}</h6>
//                   <small className="text-muted">{t("ColumnMapper.apiPayloadPreview.description.label")}</small>
//                 </Card.Header>
//                 <Card.Body>
//                   <pre className="bg-light p-3 rounded" style={{ fontSize: '0.9rem' }}>
//                     {`[
//   {
//     "dataPointCode": "${mappings.dataPointCode || '[Column Name]'}",
//     "branchData": [
// ${branchMappings
//                         .filter(mapping => (mapping.branchNameColumn || mapping.branchNameFromAPI) && mapping.branchValuesColumn)
//                         .map(mapping => `      {
//         "branchName": "${mapping.branchNameFromAPI || mapping.branchNameColumn}",
//         "value": "[Value from ${mapping.branchValuesColumn}]",
//         "uom": "${mapping.branchUomFromAPI || (mapping.branchUomColumn ? `[Value from ${mapping.branchUomColumn}]` : '')}",
//         "remark": "${mapping.branchNotesColumn ? `[Notes from ${mapping.branchNotesColumn}]` : ''}"
//       }`)
//                         .join(',\n')}
//     ]
//   }
// ]`}
//                   </pre>
//                 </Card.Body>
//               </Card>
//             </Col>
//           </Row>
//         )}
//       </>
//     )
  };

  return (
    <div data-testid="column-mapper">

      {/* Data Point Code Mapping */}
      <Row className="mb-4">
        <Col md={3}>
          <div>
            <h6 className="mb-0">{t("ColumnMapper.dataPointCodeMapping.title.label")}</h6>
            <small className="text-muted">{t("ColumnMapper.dataPointCodeMapping.description.label")}</small>

            <Form.Check
              type="checkbox"
              id="useAllDatapointsFromColumn"
              label={t("ColumnMapper.dataPointCodeMapping.useAll.label")}
              checked={useAllDatapointsFromColumn}
              onChange={(e) => {
                setUseAllDatapointsFromColumn(e.target.checked);
                const newMappings = { ...mappings };
                newMappings.useAllDatapointsFromColumn = e.target.checked;
                onMappingChange(newMappings);
              }}
              className="mb-2"
            />
          </div>
        </Col>
        <Col md={3}>
          <Form.Select
            value={mappings.dataPointCode || ''}
            onChange={(e) => handleDataPointCodeChange(e.target.value)}
            size="sm"
          >
            <option value="">{t("ColumnMapper.dataPointCodeMapping.selectColumn.label")}</option>
            {columns.map((column) => (
              <option key={column.index} value={column.name}>
                {column.name} ({column.index + 1})
              </option>
            ))}
          </Form.Select>

          {mappings.dataPointCode && mappings.dataPointCode !== 'skip' && (
            <div className="mt-2 p-2 bg-light rounded">
              <small className="text-success">
                {t("ColumnMapper.dataPointCodeMapping.mappedTo.label", { column: mappings.dataPointCode })}
              </small>
            </div>
          )}
        </Col>
        <Col>
          {!useAllDatapointsFromColumn && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowDataPointModal(true)}
            >
              <i className="fas fa-plus me-1"></i>
              {t("ColumnMapper.dataPointMapping.addDataPoint.label")}
            </Button>
          )}
        </Col>
      </Row>

      {/* Data Point Mappings */}
      {dataPointMappings.length > 0 && (
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Header className="bg-light">
                <h6 className="mb-0">{t("ColumnMapper.dataPointMapping.title.label")}</h6>
                <small className="text-muted">{t("ColumnMapper.dataPointMapping.description.label")}</small>
              </Card.Header>
              <Card.Body>
                {dataPointMappings.map((mapping, index) => (
                  <div key={mapping.id} className="mb-3 p-3 border rounded bg-light">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <h6 className="mb-0">Data Point {index + 1}</h6>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeDataPointMapping(mapping.id)}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </div>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t("ColumnMapper.dataPointMapping.selectDataPoint.label")}</Form.Label>
                          <Form.Select
                            value={mapping.dataPointId}
                            onChange={(e) => {
                              const selectedDataPoint = dataPoints.find(dp => dp.id === parseInt(e.target.value));
                              updateDataPointMapping(mapping.id, 'dataPointId', e.target.value);
                              if (selectedDataPoint) {
                                updateDataPointMapping(mapping.id, 'dataPointCode', selectedDataPoint.code);
                                updateDataPointMapping(mapping.id, 'dataPointName', selectedDataPoint.name);
                              }
                            }}
                            size="sm"
                          >
                            <option value="">{t("ColumnMapper.dataPointMapping.selectDataPointPlaceholder.label")}</option>
                            {dataPoints.map((dataPoint) => (
                              <option key={dataPoint.id} value={dataPoint.id}>
                                {dataPoint.code} - {dataPoint.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t("ColumnMapper.dataPointMapping.selectRow.label")}</Form.Label>
                          <Form.Select
                            value={mapping.selectedRow}
                            onChange={(e) => updateDataPointMapping(mapping.id, 'selectedRow', e.target.value)}
                            size="sm"
                          >
                            <option value="">{t("ColumnMapper.dataPointMapping.selectRowPlaceholder.label")}</option>
                            {selectedSheet?.data?.slice(0, 10).map((row, rowIndex) => {
                              let cellValue = 'N/A';

                              if (mappings.dataPointCode) {
                                const dataPointColumnIndex = selectedSheet.columns.findIndex(col => col.name === mappings.dataPointCode);
                                if (dataPointColumnIndex >= 0) {
                                  cellValue = row[dataPointColumnIndex] || 'N/A';
                                }
                              } else {
                                // If no datapoint column selected, show first column value
                                cellValue = row[0] || 'N/A';
                              }

                              return (
                                <option key={rowIndex} value={rowIndex}>
                                  Row {rowIndex + 1}: {cellValue}
                                </option>
                              );
                            })}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    {mapping.dataPointId && mapping.selectedRow && (
                      <div className="mt-2 p-2 bg-light rounded">
                        <small className="text-success">
                          {t("ColumnMapper.dataPointMapping.mappedToRow.label", {
                            code: mapping.dataPointCode,
                            name: mapping.dataPointName,
                            row: parseInt(mapping.selectedRow) + 1
                          })}
                        </small>
                      </div>
                    )}
                  </div>
                ))}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Branch Data Mapping */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header className="bg-light d-flex justify-content-between align-items-center">
              <div>
                <h6 className="mb-0">{t("ColumnMapper.branchDataMapping.title.label")}</h6>
                <small className="text-muted">{t("ColumnMapper.branchDataMapping.description.label")}</small>
              </div>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={addBranchMapping}
              >
                <i className="fas fa-plus me-1"></i>
                {t("ColumnMapper.branchDataMapping.addBranch.label")}
              </Button>
            </Card.Header>
            <Card.Body>
              {branchMappings.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">{t("ColumnMapper.branchDataMapping.noMappings.label")}</p>
                </div>
              ) : (
                <div>
                  {branchMappings.map((mapping, index) => (
                    <div key={mapping.id} className="mb-3 p-3 border rounded bg-light">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <h6 className="mb-0">{t("ColumnMapper.branchDataMapping.branchNumber.label", { number: index + 1 })}</h6>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeBranchMapping(mapping.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </div>

                      {/* Step 1: Branch Name */}
                      <div className="mb-4">
                        <h6 className="text-primary mb-3">
                          <i className="fas fa-tag me-2"></i>
                          Step 1: Branch Name
                        </h6>
                        <div className="row">
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold">
                                <i className="fas fa-file-excel me-1 text-success"></i>
                                From Excel File
                              </Form.Label>
                              <Form.Select
                                value={mapping.branchNameColumn || ''}
                                onChange={(e) => {
                                  const newBranchMappings = branchMappings.map(m => {
                                    if (m.id === mapping.id) {
                                      return {
                                        ...m,
                                        branchNameColumn: e.target.value,
                                        branchNameFromAPI: ''
                                      };
                                    }
                                    return m;
                                  });
                                  setBranchMappings(newBranchMappings);
                                  updateMappings(newBranchMappings, dataPointMappings);
                                }}
                                size="sm"
                                className="border-2"
                              >
                                <option value="">Choose column from Excel...</option>
                                {columns.map((column) => (
                                  <option key={column.index} value={column.name}>
                                    {column.name} ({column.index + 1})
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold">
                                <i className="fas fa-database me-1 text-primary"></i>
                                From System
                              </Form.Label>
                              <Typeahead
                                id={`branchNameFromAPI-${mapping.id}`}
                                key={`branchNameFromAPI-${mapping.id}`}
                                clearButton
                                selected={branches.filter(b => b.name === mapping.branchNameFromAPI)}
                                labelKey={(option) => option.name}
                                onChange={(selected) => {
                                  const newBranchMappings = branchMappings.map(m => {
                                    if (m.id === mapping.id) {
                                      return {
                                        ...m,
                                        branchNameFromAPI: selected.length > 0 ? selected[0].name : '',
                                        branchNameColumn: ''
                                      };
                                    }
                                    return m;
                                  });
                                  setBranchMappings(newBranchMappings);
                                  updateMappings(newBranchMappings, dataPointMappings);
                                }}
                                options={branches}
                                placeholder={isLoadingBranches ? 'Loading branches...' : 'Choose from system...'}
                                disabled={isLoadingBranches}
                                size="sm"
                              />
                            </Form.Group>
                          </div>
                        </div>
                        <small className="text-muted">
                          <i className="fas fa-info-circle me-1"></i>
                          Choose either Excel column or system branch name
                        </small>
                      </div>

                      {/* Step 2: Values */}
                      <div className="mb-4">
                        <h6 className="text-success mb-3">
                          <i className="fas fa-chart-bar me-2"></i>
                          Step 2: Values <span className="text-danger">*</span>
                        </h6>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">
                            <i className="fas fa-file-excel me-1 text-success"></i>
                            Values Column (Required)
                          </Form.Label>
                          <Form.Select
                            value={mapping.branchValuesColumn || ''}
                            onChange={(e) => updateBranchMapping(mapping.id, 'branchValuesColumn', e.target.value)}
                            size="sm"
                            className="border-2"
                          >
                            <option value="">Select column with values...</option>
                            {columns.map((column) => (
                              <option key={column.index} value={column.name}>
                                {column.name} ({column.index + 1})
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </div>

                      {/* Step 3: Unit of Measure */}
                      <div className="mb-4">
                        <h6 className="text-info mb-3">
                          <i className="fas fa-ruler me-2"></i>
                          Step 3: Unit of Measure (Optional)
                        </h6>
                        <div className="row">
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold">
                                <i className="fas fa-file-excel me-1 text-success"></i>
                                From Excel File
                              </Form.Label>
                              <Form.Select
                                value={mapping.branchUomColumn || ''}
                                onChange={(e) => {
                                  const newBranchMappings = branchMappings.map(m => {
                                    if (m.id === mapping.id) {
                                      return {
                                        ...m,
                                        branchUomColumn: e.target.value,
                                        branchUomFromAPI: ''
                                      };
                                    }
                                    return m;
                                  });
                                  setBranchMappings(newBranchMappings);
                                  updateMappings(newBranchMappings, dataPointMappings);
                                }}
                                size="sm"
                                className="border-2"
                              >
                                <option value="">Choose column from Excel...</option>
                                {columns.map((column) => (
                                  <option key={column.index} value={column.name}>
                                    {column.name} ({column.index + 1})
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold">
                                <i className="fas fa-database me-1 text-primary"></i>
                                From System
                              </Form.Label>
                              <Typeahead
                                id={`branchUomFromAPI-${mapping.id}`}
                                key={`branchUomFromAPI-${mapping.id}`}
                                clearButton
                                selected={unitsOfMeasure.filter(u => u.value === mapping.branchUomFromAPI)}
                                labelKey={(option) => option.label}
                                onChange={(selected) => {
                                  const newBranchMappings = branchMappings.map(m => {
                                    if (m.id === mapping.id) {
                                      return {
                                        ...m,
                                        branchUomFromAPI: selected.length > 0 ? selected[0].value : '',
                                        branchUomColumn: ''
                                      };
                                    }
                                    return m;
                                  });
                                  setBranchMappings(newBranchMappings);
                                  updateMappings(newBranchMappings, dataPointMappings);
                                }}
                                options={unitsOfMeasure}
                                placeholder={isLoadingUOMs ? 'Loading UOMs...' : 'Choose from system...'}
                                disabled={isLoadingUOMs}
                                size="sm"
                              />
                            </Form.Group>
                          </div>
                        </div>
                      </div>

                      {/* Step 4: Notes */}
                      <div className="mb-4">
                        <h6 className="text-secondary mb-3">
                          <i className="fas fa-sticky-note me-2"></i>
                          Step 4: Notes (Optional)
                        </h6>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">
                            <i className="fas fa-file-excel me-1 text-success"></i>
                            Notes Column
                          </Form.Label>
                          <Form.Select
                            value={mapping.branchNotesColumn || ''}
                            onChange={(e) => updateBranchMapping(mapping.id, 'branchNotesColumn', e.target.value)}
                            size="sm"
                            className="border-2"
                          >
                            <option value="">Choose column for notes...</option>
                            {columns.map((column) => (
                              <option key={column.index} value={column.name}>
                                {column.name} ({column.index + 1})
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </div>

                      {/* Preview */}
                      {(mapping.branchNameColumn || mapping.branchNameFromAPI) && mapping.branchValuesColumn && (
                        <div className="mapping-preview mt-2 p-2 bg-light rounded">
                          <small className="text-muted">
                            <strong>{t("ColumnMapper.branchDataMapping.preview.label", {
                              name: mapping.branchNameFromAPI || mapping.branchNameColumn,
                              values: mapping.branchValuesColumn,
                              uom: mapping.branchUomFromAPI || (mapping.branchUomColumn ? `from ${mapping.branchUomColumn}` : ''),
                              notes: mapping.branchNotesColumn ? t("ColumnMapper.branchDataMapping.previewWithNotes.label", { column: mapping.branchNotesColumn }) : ""
                            })}</strong>
                          </small>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Payload Preview */}
      {/* {renderPreview()} */} 

      {/* Import Button */}
      <Row className="mt-4">
        <Col className="text-center">
          <Button
            variant="success"
            size="lg"
            onClick={onImport}
            disabled={isProcessing || !canImport}
          >
            {isProcessing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                {t("ColumnMapper.import.processing.label")}
              </>
            ) : (
              t("ColumnMapper.import.button.label")
            )}
          </Button>

          {!canImport && (
            <div className="mt-2">
              <Alert variant="warning" className="d-inline-block">
                <small>
                  {t("ColumnMapper.import.warning.label")}
                </small>
              </Alert>
            </div>
          )}
        </Col>
      </Row>

      {/* Data Point Selection Modal */}
      <Modal
        show={showDataPointModal}
        onHide={() => setShowDataPointModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("ColumnMapper.dataPointMapping.selectDataPoint.label")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t("ColumnMapper.dataPointMapping.searchDataPoints.label")}</Form.Label>
                <Form.Control
                  type="text"
                  placeholder={t("ColumnMapper.dataPointMapping.searchPlaceholder.label")}
                  value={searchKey}
                  onChange={(e) => setSearchKey(e.target.value)}
                />
              </Form.Group>

              <div className="border rounded p-3" style={{ height: '400px', overflowY: 'auto' }}>
                {isLoadingDataPoints ? (
                  <div className="text-center py-4">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    {filteredDataPoints.map((dataPoint) => (
                      <div
                        key={dataPoint.id}
                        className="p-2 border-bottom"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          const newMapping = {
                            id: `datapoint-${Date.now()}`,
                            dataPointId: dataPoint.id.toString(),
                            dataPointCode: dataPoint.code,
                            dataPointName: dataPoint.name,
                            selectedRow: ''
                          };
                          const newDataPointMappings = [...dataPointMappings, newMapping];
                          setDataPointMappings(newDataPointMappings);
                          updateMappings(branchMappings, newDataPointMappings);
                          setShowDataPointModal(false);
                          setSearchKey('');
                        }}
                      >
                        <div className="fw-bold">{dataPoint.code}</div>
                        <div className="text-muted small">{dataPoint.name}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Col>
            <Col md={6}>
              <h6>{t("ColumnMapper.dataPointMapping.selectedDataPoints.label")}</h6>
              <div className="border rounded p-3" style={{ height: '400px', overflowY: 'auto' }}>
                {dataPointMappings.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    {t("ColumnMapper.dataPointMapping.noDataPointsSelected.label")}
                  </div>
                ) : (
                  <div>
                    {dataPointMappings.map((mapping, index) => (
                      <div key={mapping.id} className="p-2 border-bottom">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <div className="fw-bold">{mapping.dataPointCode}</div>
                            <div className="text-muted small">{mapping.dataPointName}</div>
                          </div>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removeDataPointMapping(mapping.id)}
                          >
                            <i className="fas fa-times"></i>
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDataPointModal(false)}>
            {t("Cancel.label")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ColumnMapper; 